/** Dispute submit / resolve orchestration (keeps UI state in the caller). */

import databaseAPI from '../../api/databaseAPI.js';
import emailService from '../../api/emailService.js';

export const buildDisputeFormData = ({ orderId, reason, comment, photos }) => {
  const formData = new FormData();
  formData.append('order_id', orderId);
  formData.append('reason', reason);
  formData.append('comment', comment);
  photos.forEach((file) => {
    formData.append('photos', file);
  });
  return formData;
};

export const submitDisputeAction = async ({
  disputeOrderId,
  disputeReason,
  disputeComment,
  disputePhotos,
  orders,
  alert: alertFn = window.alert,
  onClose,
  onRefresh,
  api = databaseAPI,
  mailer = emailService,
  skipValidation = false,
}) => {
  if (!skipValidation) {
    if (!disputeReason.trim()) {
      alertFn('⚠️ Please select a dispute reason');
      return { ok: false };
    }

    if (!disputeComment.trim()) {
      alertFn('⚠️ Please add a comment describing the issue');
      return { ok: false };
    }

    if (!disputePhotos.length) {
      alertFn('⚠️ Please upload at least one issue image');
      return { ok: false };
    }
  }

  try {
    const order = orders.find(o => o.id === disputeOrderId);

    const formData = buildDisputeFormData({
      orderId: disputeOrderId,
      reason: disputeReason,
      comment: disputeComment,
      photos: disputePhotos,
    });

    const disputeResult = await api.createDisputeWithFiles(formData);

    if (!disputeResult.success) {
      alertFn(`❌ Failed to create dispute: ${disputeResult.error}`);
      return { ok: false };
    }

    try {
      const orderDetails = {
        email: order.email,
        name: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : '',
        order_name: order.name,
        line_items: order.line_items
      };

      await mailer.sendStatusUpdateEmail('dispute', orderDetails, '', order.is_preorder);

      await api.logEmail(
        disputeOrderId,
        'dispute',
        'dispute',
        order.email,
        `Dispute - Order ${order.name}`,
        'sent'
      );
    } catch (emailError) {
      await api.logEmail(
        disputeOrderId,
        'dispute',
        'dispute',
        order.email,
        `Dispute - Order ${order.name}`,
        'failed',
        emailError.message
      );
    }

    alertFn('✅ Issue Reported submitted successfully!\nWe will resolve this within 24 hours.');
    if (typeof onClose === 'function') onClose();
    if (typeof onRefresh === 'function') await onRefresh();
    return { ok: true };
  } catch (error) {
    alertFn('❌ Failed to submit dispute: ' + error.message);
    return { ok: false, error };
  }
};

export const resolveDisputeAction = async ({
  orderId,
  resolution,
  orders,
  showConfirm,
  showNotification,
  onRefresh,
  api = databaseAPI,
  mailer = emailService,
}) => {
  const confirmMessage = resolution === 'refund'
    ? 'Are you sure you want to process a REFUND for this order?'
    : 'Are you sure you want to APPROVE REPLACEMENT for this order?';

  const shouldResolve = await showConfirm({
    title: 'Resolve Dispute',
    message: confirmMessage,
    confirmText: resolution === 'refund' ? 'Process Refund' : 'Approve Replacement',
    cancelText: 'Cancel'
  });
  if (!shouldResolve) return { ok: false, cancelled: true };

  try {
    const order = orders.find(o => o.id === orderId);

    const resolveResult = await api.resolveDispute(orderId, resolution);
    if (!resolveResult.success) {
      showNotification(`❌ Failed to resolve dispute: ${resolveResult.error}`, 'error', 6000);
      return { ok: false };
    }

    if (resolution === 'refund') {
      try {
        const orderDetails = {
          email: order.email,
          name: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : '',
          order_name: order.name,
          line_items: order.line_items
        };

        await mailer.sendStatusUpdateEmail('refunded', orderDetails, '', order.is_preorder);

        await api.logEmail(
          orderId,
          'refund',
          'refunded',
          order.email,
          `Refund Processed - Order ${order.name}`,
          'sent'
        );
      } catch (emailError) {
        await api.logEmail(
          orderId,
          'refund',
          'refunded',
          order.email,
          `Refund Processed - Order ${order.name}`,
          'failed',
          emailError.message
        );
      }

      showNotification('✅ Refund processed successfully!', 'success');
    } else if (resolution === 'replacement') {
      try {
        const orderDetails = {
          email: order.email,
          name: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : '',
          order_name: order.name,
          line_items: order.line_items
        };

        await mailer.sendStatusUpdateEmail('replacement_approved', orderDetails, '', order.is_preorder);

        await api.logEmail(
          orderId,
          'status_update',
          'replacement_approved',
          order.email,
          `Replacement Approved - Order ${order.name}`,
          'sent'
        );
      } catch (emailError) {
        await api.logEmail(
          orderId,
          'status_update',
          'replacement_approved',
          order.email,
          `Replacement Approved - Order ${order.name}`,
          'failed',
          emailError.message
        );
      }

      showNotification('✅ Replacement approved successfully!', 'success');
    }

    if (typeof onRefresh === 'function') await onRefresh();
    return { ok: true };
  } catch (error) {
    showNotification('❌ Failed to resolve dispute: ' + error.message, 'error');
    return { ok: false, error };
  }
};
